// Static "action" barcodes for the scan-driven part flow on a dismantling job.
//
// A part tag scanned on the job opens the part action modal; scanning one of
// these codes then performs the chosen action on that pending part, so an
// operator can work hands-off. They encode as "a|{action}" alongside the
// existing "v|", "p|" and "bin|" prefixes (see ScannerContext), and carry no
// part id on purpose - the same set of codes works for every part, so they
// can be shown on screen or printed once and kept at the bench.

export const SCAN_ACTION_PREFIX = "a";

export type ScanAction = "removed" | "damaged" | "missing" | "notaccessible" | "note" | "cancel";

export const SCAN_ACTIONS: readonly ScanAction[] = [
  "removed",
  "damaged",
  "missing",
  "notaccessible",
  "note",
  "cancel",
];

const isScanAction = (value: string): value is ScanAction =>
  (SCAN_ACTIONS as readonly string[]).includes(value);

/** The barcode payload for an action, e.g. "a|damaged". */
export const scanActionCode = (action: ScanAction): string => `${SCAN_ACTION_PREFIX}|${action}`;

/**
 * Reads an action from the delimited scan parts ("a|{action}"), or null if the
 * scan isn't an action code. Prefix and action are matched case-insensitively
 * since scanners configured for a different keyboard layout can shift case.
 */
export const parseScanAction = (parts: string[]): ScanAction | null => {
  if (parts.length < 2) return null;
  if (parts[0]?.toLowerCase() !== SCAN_ACTION_PREFIX) return null;
  const action = parts[1]?.trim().toLowerCase() ?? "";
  return isScanAction(action) ? action : null;
};
